import { AxiosResponse } from 'axios';
import { bunja } from 'bunja';
import { addMinutes } from 'date-fns';
import { atom } from 'jotai';
import { atomWithQuery } from 'jotai-tanstack-query';

import { getNetworkStatusPolicyApi } from '@flipster/apis/v1/network-status-policy';
import { GetNetworkStatusPolicyResponse } from '@flipster/apis/v1/network-status-policy/types';
import { authBunja } from '@flipster/features/src/shared/auth/state';
import { rttEmitter } from '@flipster/socket/rtt-emitter';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';
import type { RttData } from '@flipster/utils/rtt-tracking';

export type NetworkConditionType = 'good' | 'unstable' | 'poor';

export const networkConditionBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const { isCookieLoggedInAtom } = bunja.use(authBunja);
  const rttAtom = atom<RttData | null>(null);
  rttAtom.debugLabel = 'rttAtom';

  const dismissedUntilAtom = atom<number | null>(null);
  dismissedUntilAtom.debugLabel = 'dismissedUntilAtom';

  const networkStatusPolicyAtom = atomWithQuery((get) => ({
    queryKey: ['getNetworkStatusPolicyApi'],
    queryFn: getNetworkStatusPolicyApi,
    select: (response: AxiosResponse<GetNetworkStatusPolicyResponse>) => response.data,
    enabled: get(isCookieLoggedInAtom),
    staleTime: Infinity,
    gcTime: Infinity,
  }));

  const rawNetworkConditionAtom = atom<NetworkConditionType>((get) => {
    const rttData = get(rttAtom);
    if (!rttData || rttData.rttMs === null) {
      return 'good';
    }

    const policyQuery = get(networkStatusPolicyAtom);
    const policy = policyQuery.data;
    if (!policy) {
      return 'good';
    }
    const poorThreshold = policy.poorThresholdMs;
    const unstableThreshold = policy.unstableThresholdMs;

    if (rttData.rttMs >= poorThreshold) {
      return 'poor';
    }
    if (rttData.rttMs >= unstableThreshold) {
      return 'unstable';
    }
    return 'good';
  });
  rawNetworkConditionAtom.debugLabel = 'rawNetworkConditionAtom';

  const networkConditionAtom = atom<NetworkConditionType>((get) => {
    const rawCondition = get(rawNetworkConditionAtom);
    const dismissedUntil = get(dismissedUntilAtom);
    if (dismissedUntil && Date.now() < dismissedUntil) {
      return 'good';
    }

    return rawCondition;
  });
  networkConditionAtom.debugLabel = 'networkConditionAtom';

  const dismissNetworkBanner = () => {
    store.set(dismissedUntilAtom, addMinutes(Date.now(), 3).getTime());
  };

  const listener = (rttData: RttData) => {
    store.set(rttAtom, rttData);
  };

  bunja.effect(() => {
    rttEmitter.on('update', listener);
    return () => {
      rttEmitter.off('update', listener);
    };
  });

  return {
    rttAtom,
    networkConditionAtom,
    networkStatusPolicyAtom,
    dismissNetworkBanner,
  };
});
networkConditionBunja.debugLabel = 'networkConditionBunja';
